import { TrendingDown, TrendingUp, Minus } from "lucide-react";
import type { PriceHistory } from "@/lib/types";

/**
 * Adaptive price indicator. Price history isn't always over an extended period,
 * so the indicator shape follows the data (honest — never a fake trend):
 *   - 3+ points  → sparkline (a real trend line) + labels.
 *   - thin (≤2)  → a low·now·high RANGE BAR with a delta, which reads
 *                  truthfully even from a couple of observations.
 *   - nothing knowable → renders nothing.
 *
 * FACTS vs PIXELS: every number rendered here (now/low/high/checks/spanDays and
 * both deltas) comes from `history`'s full-history aggregates. `history.points`
 * is a day-bucketed DRAWING series and is used only for the polyline geometry —
 * never to compute a displayed low, high, count or span. Deriving labels from
 * the series is exactly the bug this component was corrected for.
 *
 * compact=true is the slimmer variant used in option rows.
 */
export default function PriceHistoryStrip({
  history,
  compact = false,
}: {
  history: PriceHistory;
  compact?: boolean;
}) {
  const pts = history.points.filter((p) => Number.isFinite(p.price));
  const prices = pts.map((p) => p.price);
  const {
    now,
    low,
    high,
    deltaPct,
    checks,
    spanDays,
    trend,
    recentTrend,
    recentDeltaPct,
    recentWindowDays,
  } = history;

  const lo = low;
  const hi = high;
  const nowVal = now;

  const dropped = trend === "down";
  const rose = trend === "up";
  const stroke = dropped
    ? "rgb(4 120 87)" // emerald-700
    : rose
      ? "rgb(180 83 9)" // amber-700
      : "rgb(165 162 154)"; // warm neutral
  const Icon = dropped ? TrendingDown : rose ? TrendingUp : Minus;

  const deltaAbs = deltaPct != null ? Math.round(Math.abs(deltaPct)) : null;
  const deltaArrow = dropped ? "▼" : rose ? "▲" : "–";

  // Short-window move, carried alongside the overall one: a multi-week trend
  // can hide a sharp recent reversal, so we state both and name each period.
  const recentAbs = recentDeltaPct != null ? Math.round(Math.abs(recentDeltaPct)) : null;
  const recentArrow = recentTrend === "down" ? "▼" : recentTrend === "up" ? "▲" : "–";

  // ── Extended history → sparkline ──────────────────────────────────────────
  if (pts.length >= 3) {
    // Geometry only — the polyline has to scale to the points it actually
    // draws. These are NOT the reported low/high (which come from `history`).
    const min = Math.min(...prices);
    const max = Math.max(...prices);
    const span = max - min || 1;
    const W = 132;
    const H = 22;
    const pad = 2;
    const stepX = (W - pad * 2) / (pts.length - 1);
    const coords = prices.map((p, i) => {
      const x = pad + i * stepX;
      const y = pad + (1 - (p - min) / span) * (H - pad * 2);
      return [x, y] as const;
    });
    const [lx, ly] = coords[coords.length - 1];
    const sparkline = (
      <svg
        width={W}
        height={H}
        viewBox={`0 0 ${W} ${H}`}
        className="shrink-0"
        role="img"
        aria-label={
          lo != null && hi != null
            ? `Price history${spanDays != null ? ` over ${spanDays} days` : ""}: $${lo} to $${hi}`
            : "Price history"
        }
      >
        <polyline points={coords.map(([x, y]) => `${x},${y}`).join(" ")} fill="none" stroke={stroke} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
        <circle cx={lx} cy={ly} r="2.2" fill={stroke} />
      </svg>
    );
    if (compact) {
      const trendWord = dropped ? "falling" : rose ? "rising" : "stable";
      return (
        <div className="mt-1 flex items-center gap-2 pl-1">
          {sparkline}
          <span className="text-[10px] tabular-nums text-muted-foreground">
            <span className="font-mono">${lo}–${hi}</span> · <span style={{ color: stroke }}>{trendWord}</span>
          </span>
        </div>
      );
    }
    // Every phrase names the period it describes, so no figure can be read as
    // covering a window it doesn't cover.
    const parts: string[] = [];
    if (now != null) parts.push(`Now $${now}`);
    if (low != null) parts.push(`low $${low}`);
    if (high != null) parts.push(`high $${high}`);
    if (deltaAbs != null && deltaAbs > 0) parts.push(`${deltaArrow} ${deltaAbs}% since tracking began`);
    if (recentAbs != null && recentAbs > 0) parts.push(`${recentArrow} ${recentAbs}% in the last ${recentWindowDays} days`);
    if (checks > 0) parts.push(spanDays != null ? `${checks} checks over ${spanDays} days` : `${checks} checks`);
    return (
      <div className="mt-1 flex items-start gap-2 pl-1">
        {sparkline}
        <div className="flex flex-wrap items-center gap-1 text-[10px] text-muted-foreground">
          <Icon className="h-3 w-3 shrink-0" style={{ color: stroke }} aria-hidden="true" />
          <span className="tabular-nums">{parts.join(" · ")}</span>
        </div>
      </div>
    );
  }

  // ── Thin history → range bar + "since last check" ticker ──────────────────
  if (nowVal == null && lo == null && hi == null) return null;

  const hasRange = lo != null && hi != null && hi > lo;
  const markerPct = hasRange && nowVal != null
    ? Math.min(100, Math.max(0, ((nowVal - lo!) / (hi! - lo!)) * 100))
    : null;

  // `deltaPct` is the move since the FIRST-EVER check, not since the previous
  // one — the old "since last check" wording named the wrong period.
  const ticker = deltaAbs != null && deltaAbs > 0
    ? `${deltaArrow} ${deltaAbs}% since tracking began`
    : checks > 1
      ? "steady since tracking began"
      : "first look";

  return (
    <div className={"mt-1 pl-1 " + (compact ? "text-[10px]" : "text-[11px]")}>
      <div className="flex items-center gap-2">
        {hasRange ? (
          <div className="relative h-1.5 flex-1 rounded-full bg-muted" aria-hidden="true">
            {markerPct != null && (
              <span
                className="absolute top-1/2 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full"
                style={{ left: `${markerPct}%`, background: stroke }}
              />
            )}
          </div>
        ) : (
          <span className="tabular-nums text-muted-foreground">
            {nowVal != null ? `Now $${nowVal}` : ""}
          </span>
        )}
        <span className="shrink-0 whitespace-nowrap tabular-nums" style={{ color: stroke }}>
          <Icon className="mr-0.5 inline h-3 w-3 align-[-2px]" aria-hidden="true" />
          {ticker}
        </span>
      </div>
      {hasRange && (
        <div className="mt-0.5 flex justify-between tabular-nums text-muted-foreground">
          <span>low ${lo}</span>
          {nowVal != null && <span style={{ color: stroke }}>now ${nowVal}</span>}
          <span>high ${hi}</span>
        </div>
      )}
    </div>
  );
}
